import { isFrameId } from "@/api/frame-listing";
import type { MarkdownLinkClickHandler } from "@/components/ui/markdown-content";
import { presentFrames } from "@/lib/canvas-presentation";
import { activateFrameInCurrentContext } from "@/lib/focus-frame-activation";
import { openExternalUrl } from "@/lib/open-external-url";
import {
  loadedFrameIdByTitle,
  openFrameTitledNow,
  openFramesNow,
} from "@/lib/open-frames";
import { usePresentationStore } from "@/stores/presentation-store";
import { useFramesStore } from "@/stores/frames-store";
import { useDesktopMode } from "@/stores/desktop-mode-store";

const FRAME_ID_PREFIX = "denker://frame/";
const FRAME_TITLE_PREFIX = "denker://frame-title/";
const SAFE_EXTERNAL_PROTOCOLS = new Set(["http:", "https:", "mailto:", "tel:"]);

function safeDecode(value: string): string | null {
  try {
    return decodeURIComponent(value);
  } catch {
    return null;
  }
}

function safeExternalUrl(href: string): string | null {
  const trimmed = href.trim();
  if (!/^[a-z][a-z0-9+.-]*:/i.test(trimmed) && !trimmed.startsWith("//")) {
    return null;
  }
  try {
    const url = new URL(
      trimmed.startsWith("//")
        ? `${window.location.protocol}${trimmed}`
        : trimmed,
    );
    return SAFE_EXTERNAL_PROTOCOLS.has(url.protocol) ? url.href : null;
  } catch {
    return null;
  }
}

/** The old canvas's open; the wall opens through `@/lib/open-frames`. */
function openFrame(frameId: string): void {
  const { presentation } = usePresentationStore.getState();
  const desktop = useDesktopMode.getState();
  if (desktop.mode === "compact" && presentation?.variant !== "focus") {
    presentFrames({ frameIds: [frameId] });
    return;
  }
  activateFrameInCurrentContext(frameId);
}

export const handleConversationFrameLink: MarkdownLinkClickHandler = (
  href,
  _text,
) => {
  const normalizedHref = href.trim();
  let frameId: string | null = null;

  if (normalizedHref.startsWith(FRAME_ID_PREFIX)) {
    const candidateId = normalizedHref.slice(FRAME_ID_PREFIX.length);
    if (!isFrameId(candidateId)) return true;
    // The wall fetches a frame no page brought.
    if (openFramesNow([candidateId], "navigate")) return true;
    frameId = useFramesStore.getState().framesById[candidateId]
      ? candidateId
      : null;
  } else if (normalizedHref.startsWith(FRAME_TITLE_PREFIX)) {
    const title = safeDecode(normalizedHref.slice(FRAME_TITLE_PREFIX.length));
    if (title === null) return true;
    if (openFrameTitledNow(title, "navigate")) return true;
    frameId = loadedFrameIdByTitle(title);
  }

  if (frameId) {
    openFrame(frameId);
    return true;
  }
  if (normalizedHref.startsWith("denker://")) return true;

  const externalUrl = safeExternalUrl(normalizedHref);
  if (externalUrl) {
    void openExternalUrl(externalUrl);
    return true;
  }

  // Keep ordinary relative and hash links as normal anchors.
  return false;
};
